'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import InputMask from '@mona-health/react-input-mask';
import { TextFieldProps } from '@src/v3/interfaces/components/coreUI/textField';
import { formatNumberWithCommas } from '@src/v3/utils/helper';

const sizeClasses = {
  dark: {
    sm: 'h-12',
    md: 'h-[52px]',
    lg: 'h-[60px]',
  },
  light: {
    sm: 'h-10 sm:h-12',
    md: 'h-12 sm:h-14',
    lg: 'h-14 sm:h-[62px]',
  },
};

const textSizeClasses = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base sm:text-lg',
};

const variantClasses = {
  dark: {
    border: 'border-gray-600 sm:border-gray-700',
    text: 'text-gray-1000 placeholder:text-gray-700',
    focus:
      'focus-visible:border-ring focus:ring-black-dark focus-visible:ring-black-dark',
    error:
      'border-red-700 ring-red-700/20 focus-visible:border-red-700 focus-visible:ring-red-700/50',
  },
  light: {
    border: 'border-gray-700 sm:border-gray-400',
    text: 'text-gray-700 placeholder:text-gray-700',
    focus:
      'focus-visible:border-ring focus:ring-black-dark focus-visible:ring-black-dark',
    error:
      'border-red-700 ring-red-700/20 focus-visible:border-red-700 focus-visible:ring-red-700/50',
  },
};

function TextField({
  size = 'md',
  variant = 'dark',
  label,
  labelPosition = 'top',
  requiredLabel = false,
  showLabel = true,
  helperText,
  helperIcon,
  mask,
  maskPlaceholder = 'x',
  startAdornment,
  endAdornment,
  error = false,
  errorMessage,
  className,
  inputClassName,
  containerClassName,
  labelClassName,
  errorMessageClassName,
  type = 'text',
  countryCode,
  onChange,
  value,
  numericOnly = false,
  formatDisplayOnly = false,
  allowDecimals = false,
  ...props
}: TextFieldProps) {
  const inputClasses = clsx(
    'w-full border rounded-xl bg-transparent transition-colors outline-none',
    'focus:ring-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none ring-offset-0',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
    sizeClasses[variant][size],
    textSizeClasses[size],
    variantClasses[variant].border,
    variantClasses[variant].text,
    {
      'pl-3 pr-3': !startAdornment && !endAdornment,
      'pl-10 pr-3': startAdornment && !endAdornment,
      'pl-3 pr-10': !startAdornment && endAdornment,
      'pl-10 pr-10': startAdornment && endAdornment,
      '!border-red-700': error,
    },
    {
      [variantClasses[variant].focus]: !error,
      [variantClasses[variant].error]: error,
    },
    inputClassName,
    countryCode
      ? 'flex-1 min-w-0 !p-4 rounded-l-none rounded-r-xl border-l-0 border-r-0 border-t-0 ring-offset-0'
      : ''
  );

  const containerClasses = clsx('w-full', containerClassName);

  const wrapperClasses = clsx(
    'flex relative',
    countryCode
      ? [
          'flex items-center rounded-xl overflow-hidden border bg-transparent',
          variantClasses[variant].border,
          sizeClasses[variant][size],
          error && '!border-red-700',
          !error &&
            variantClasses[variant].focus
              .replace(/focus:/g, 'focus-within:')
              .replace(/focus-visible:/g, 'focus-within:'),
        ]
      : null
  );

  const labelClasses = clsx(
    'block text-base sm:text-lg font-normal mb-3 !leading-4 text-gray-600 sm:text-gray-700',
    {
      'text-foreground': !error,
      'text-destructive': error,
    },
    labelClassName
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (numericOnly) {
      const inputValue = e.target.value;
      // Allow digits, and optionally decimals
      const pattern = allowDecimals ? /^\d*\.?\d*$/ : /^\d+$/;
      if (inputValue === '' || pattern.test(inputValue)) {
        onChange?.(e);
      }
    } else {
      onChange?.(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only handle Enter key for single-line text inputs (not textarea)
    // This ensures we only respond to the mobile keyboard action button (Next/Go/Done)
    if (
      e.key === 'Enter' &&
      e.currentTarget.tagName === 'INPUT' &&
      e.currentTarget.type !== 'submit' &&
      e.currentTarget.type !== 'button'
    ) {
      e.preventDefault();

      const { form } = e.currentTarget;
      if (form) {
        // Get all focusable form elements
        const focusableElements = Array.from(
          form.querySelectorAll<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
          >(
            'input:not([disabled]):not([type="hidden"]):not([type="submit"]):not([type="button"]), select:not([disabled]), textarea:not([disabled])'
          )
        );

        const currentIndex = focusableElements.indexOf(e.currentTarget);
        const nextElement = focusableElements[currentIndex + 1];

        if (nextElement) {
          // Focus next field
          nextElement.focus();
        } else {
          // Last field - blur to close keyboard
          e.currentTarget.blur();
        }
      } else {
        // Not in a form - just close keyboard
        e.currentTarget.blur();
      }
    }

    // Call original onKeyDown if provided
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  const renderInput = () => {
    // Handle formatDisplayOnly - display with commas, store without commas
    if (formatDisplayOnly && numericOnly) {
      const displayValue =
        value && value !== '' ? formatNumberWithCommas(Number(value)) : '';

      return (
        <input
          type="text"
          name={props.name}
          value={displayValue}
          onChange={(e) => {
            // Remove commas and pass only digits to onChange
            const rawValue = e.target.value.replace(/,/g, '');
            const syntheticEvent = {
              ...e,
              target: {
                ...e.target,
                value: rawValue,
                name: props.name,
              },
            };
            handleChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
          }}
          onKeyDown={handleKeyDown}
          className={inputClasses}
          {...props}
        />
      );
    }

    const inputProps = {
      type,
      value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      className: inputClasses,
      ...props,
    };

    if (mask) {
      return (
        <InputMask
          mask={mask}
          maskPlaceholder={maskPlaceholder}
          {...inputProps}
          // Safari fix: Prevent zoom and cursor issues on iOS when smart app banner is present
          style={{ touchAction: 'manipulation' }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            // Call original onFocus if provided
            props.onFocus?.(e);
            if (countryCode) {
              // Safari fix: scroll only when needed to avoid shifting content
              setTimeout(() => {
                const el = e.target;
                const rect = el.getBoundingClientRect();
                const inView =
                  rect.top >= 0 &&
                  rect.bottom <=
                    (typeof window !== 'undefined' ? window.innerHeight : 0);
                if (!inView) {
                  el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
                }
              }, 100);
            } else {
              setTimeout(() => {
                e.target.scrollIntoView({
                  block: 'center',
                  behavior: 'smooth',
                });
              }, 100);
            }
          }}
        />
      );
    }

    if (props.codePlaceholder) {
      inputProps.placeholder = '— — — — — —';
      inputProps.className = clsx(
        inputClasses,
        'placeholder:!text-gray-800 placeholder:font-bold placeholder:text-lg'
      );
    }

    return <input {...inputProps} />;
  };

  const renderCountryCode = () => {
    if (!countryCode) return null;

    const countryCodeClasses = clsx(
      'flex shrink-0 items-center justify-center min-w-[52px] border-r rounded-l-xl text-base focus-visible:ring-0 focus:ring-0 self-stretch',
      variant === 'dark'
        ? 'border-gray-700 text-gray-700'
        : 'border-gray-300 text-gray-500'
    );

    return (
      <div className={countryCodeClasses}>
        {typeof countryCode === 'string' ? countryCode : countryCode}
      </div>
    );
  };

  const renderLabel = () => {
    if (!showLabel || !label) return null;

    return (
      <label className={labelClasses}>
        {label}
        {requiredLabel && <span className="text-destructive ml-1">*</span>}
      </label>
    );
  };

  const renderHelperText = () => (
    <>
      {helperText && (
        <div className="mt-2 sm:mt-3 flex items-center">
          {helperIcon && (
            <div className="w-5 h-5 flex items-center justify-center mr-2">
              {helperIcon}
            </div>
          )}
          <p className="sm:!leading-4 !leading-6 text-sm text-gray-700 sm:text-gray-600">
            {helperText}
          </p>
        </div>
      )}
      {error && errorMessage && (
        <div className="mt-1 flex items-center">
          {helperIcon && (
            <div className="w-5 h-5 flex items-center justify-center mr-2">
              {helperIcon}
            </div>
          )}
          <p className={clsx('text-sm text-red-700', errorMessageClassName)}>
            {errorMessage}
          </p>
        </div>
      )}
    </>
  );

  const renderSections = () => {
    if (!startAdornment && !endAdornment) return null;

    return (
      <>
        {startAdornment && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-muted-foreground">
            {startAdornment}
          </div>
        )}
        {endAdornment && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-muted-foreground">
            {endAdornment}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={clsx(containerClasses, className)}>
      {labelPosition === 'top' && renderLabel()}

      <div className={wrapperClasses}>
        {renderSections()}
        {renderCountryCode()}
        {renderInput()}
      </div>

      {labelPosition === 'bottom' && renderLabel()}
      {renderHelperText()}
    </div>
  );
}

export default TextField;
